/**
 * Diagram Preview - Simplified preview for touch devices
 * Works with CSS-only modal approach, no DOM manipulation
 */

import { isTouchDevice } from './touch-detector.js';
import { diagramModal } from './diagram-modal.js';

export class DiagramPreview {
  private viewButton: HTMLButtonElement | null = null;
  private greyOverlay: HTMLDivElement | null = null;
  private isInitialized: boolean = false;
  
  /**
   * Initialize preview mode for touch devices
   */
  initialize(diagramContainer: HTMLElement): void {
    // Only initialize for touch devices
    if (!isTouchDevice() || this.isInitialized) {
      return;
    }
    
    // Add class for CSS to handle everything
    diagramContainer.classList.add('touch-preview-mode');
    
    // Create and add the view button
    this.createViewButton(diagramContainer);
    
    // Add styles
    this.addStyles();
    
    this.isInitialized = true;
  }
  
  /**
   * Add preview styles
   */
  private addStyles(): void {
    if (document.getElementById('diagram-preview-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'diagram-preview-styles';
    style.textContent = `
      /* Touch preview mode styles */
      .touch-preview-mode {
        position: relative;
        pointer-events: none;
      }
      
      /* Hide controls in preview */
      .touch-preview-mode .diagram-controls {
        display: none !important;
      }
      
      /* Hide info button in preview */
      .touch-preview-mode .diagram-info-button {
        display: none !important;
      }
      
      /* Hide disclaimer and CTA in preview */
      .touch-preview-mode .diagram-disclaimer,
      .touch-preview-mode .diagram-cta-container,
      .touch-preview-mode #partialReportOverlay {
        display: none !important;
      }
      
      /* Constrain height for touch preview - using same calculation as regular view */
      #journeyContent.tab-content:has(.touch-preview-mode) {
        height: ${Math.max(500, Math.floor(window.innerHeight))}px !important;
        max-height: ${Math.max(500, Math.floor(window.innerHeight))}px !important;
        overflow: hidden !important;
      }
      
      /* Also constrain the diagram container within touch preview */
      .touch-preview-mode.diagram-container {
        height: ${Math.max(500, Math.floor(window.innerHeight))}px !important;
        max-height: ${Math.max(500, Math.floor(window.innerHeight))}px !important;
        overflow: hidden !important;
      }
      
      /* Blur overlay with subtle tint */
      .diagram-preview-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        backdrop-filter: blur(2px);
        background-color: rgba(255, 255, 255, 0.15);
        z-index: 9;
        pointer-events: none;
      }
      
      /* View button with shadow for better visibility */
      .touch-preview-mode .view-fullscreen-button {
        position: absolute;
        top: min(400px, 50%);
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 10;
        pointer-events: auto;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2);
      }
      
      /* When in fullscreen, remove preview constraints */
      .touch-preview-mode.fullscreen-mode {
        pointer-events: auto !important;
      }
      
      .fullscreen-mode .diagram-preview-overlay,
      .fullscreen-mode .view-fullscreen-button {
        display: none !important;
      }
      
      /* Show everything in fullscreen - higher specificity to override preview mode */
      .touch-preview-mode.fullscreen-mode .diagram-controls {
        display: flex !important;
      }
      
      .touch-preview-mode.fullscreen-mode .diagram-info-button {
        display: flex !important;
      }
      
      .touch-preview-mode.fullscreen-mode .diagram-disclaimer,
      .touch-preview-mode.fullscreen-mode #partialReportOverlay {
        display: block !important;
      }
      
      /* Remove height constraints in fullscreen mode */
      #journeyContent.tab-content:has(.touch-preview-mode.fullscreen-mode) {
        height: auto !important;
        max-height: none !important;
        overflow: auto !important;
      }
      
      .touch-preview-mode.fullscreen-mode.diagram-container {
        height: auto !important;
        max-height: none !important;
        overflow: auto !important;
      }
    `;
    
    document.head.appendChild(style);
  }
  
  /**
   * Create the view button overlay
   */
  private createViewButton(diagramContainer: HTMLElement): void {
    // Create overlay to grey out the diagram
    this.greyOverlay = document.createElement('div');
    this.greyOverlay.className = 'diagram-preview-overlay';
    
    // Create button
    this.viewButton = document.createElement('button');
    this.viewButton.className = 'brand-button-compact view-fullscreen-button';
    this.viewButton.textContent = 'View Fullscreen';
    this.viewButton.setAttribute('aria-label', 'View diagram in fullscreen');
    
    // Add click handler
    this.viewButton.addEventListener('click', () => {
      this.openModal(diagramContainer);
    });
    
    // Add overlay and button to container
    diagramContainer.appendChild(this.greyOverlay);
    diagramContainer.appendChild(this.viewButton);
  }
  
  /**
   * Open the diagram modal
   */
  private openModal(diagramContainer: HTMLElement): void {
    // Just open the modal - CSS handles everything else
    diagramModal.open(diagramContainer);
  }
}

// Export singleton instance
export const diagramPreview = new DiagramPreview();

// Make available globally for non-module scripts
declare global {
  interface Window {
    diagramPreview?: DiagramPreview;
  }
}

if (typeof window !== 'undefined') {
  window.diagramPreview = diagramPreview;
}